/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
	Output,
	EventEmitter,
	ChangeDetectorRef,
	ViewChild,
	ElementRef,
	HostBinding,
	ContentChild,
	Optional,
} from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	UntypedFormBuilder,
	UntypedFormControl,
	NgControl,
	ValidationErrors,
	ValidatorFn,
	FormGroupDirective,
	FormControl,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { mapTo, takeUntil } from 'rxjs/operators';
import * as appStrings from '@touchplan/strings';
import { MatFormFieldControl } from '@angular/material/form-field';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { FileUploadCustomSlotDirective } from './file-upload-custom-slot';

//Sample usage
//Standard
// <mat-form-field style="width: 400px" appearance="outline">
// 	<mat-label>{{ 'Schedule Template' }}</mat-label>
// 	<tp-file-upload
// 		#fileUploadController
// 		[formControl]="fileContent"
// 		[acceptedFileTypes]="['.xls', '.xlsx']"
// 		[validationErrors]="[{ errorCode: 'dataFormatIncorrect', message: 'file data format is not correct' }]"
// 		startupMessage="No template uploaded"
// 		successMessage="Template uploaded successfully"
// 	></tp-file-upload>
// 	<mat-hint>{{ fileUploadController.hintMessages.get(fileUploadController.uploadState) }}</mat-hint>
// 	<mat-error *ngIf="fileUploadController.getErrorMessage()">{{
// 		fileUploadController.getErrorMessage()
// 	}}</mat-error>
// </mat-form-field>

//Custom
// <tp-file-upload
// 	[formControl]="fileContent2"
// 	[acceptedFileTypes]="['.xls', '.xlsx']"
// 	[validationErrors]="[{ errorCode: 'dataFormatIncorrect', message: 'file data format is not correct' }]"
// >
// 	<ng-template fileUploadCustomSlot>
// 		<h2>Click the whole thing</h2>
// 	</ng-template>
// </tp-file-upload>

/**
 * expects an input supported by the accept attr
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept#unique_file_type_specifiers
 * The File object contains a type property with the mime type. So in the interest of brevity
 * only _that_ will actually be checked as far as validation is concerned. This means that the acceptedFileTypes will need to specify that.
 * validTypes = ['.xls', '.xlsx', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
 * fileControl = the formControl that is bound to the file input
 * The file input accept property also supports mime types, so '.xls', '.xlsx' is probably unnecessary. But... readability?
 *
 * Currently this validator is applied dynamically to the ngControl (external control/ "self" control) on ngInit.
 * When used as part of tp-file-upload it allows for the acceptedFileTypes to be used both as the "suggestion" of type and the enforcement
 *
 * *NOTE* this validator does not bind to the attached control, but instead relies on a passed in form control.
 * This seems like a side effect of this component's history and dividing itself up into multiple internal controls
 */
export function validFileTypeValidator(validTypes: readonly string[], fileControl: FormControl): ValidatorFn {
	return (): ValidationErrors | null => {
		if (!validTypes || validTypes.length === 0) {
			return null;
		}
		//validator only applies if set to File, only validates the first file
		if (!fileControl || !fileControl.value || !fileControl.value.length || !fileControl.value[0].type) {
			return null;
		}

		const type: string = fileControl.value[0].type;

		const noMatches = validTypes.every((supportedType) => supportedType !== type);

		if (noMatches) {
			return {
				dataFormatIncorrect: true,
			};
		}
		return null;
	};
}

export interface IValidationError {
	errorCode: string;
	message: string;
}
export enum FileUploadState {
	Waiting = 'waiting',
	Loading = 'loading',
	Succeeded = 'succeeded',
	Failed = 'failed',
}
export enum UploadOutputFormat {
	BinaryString = 'binaryString',
	Text = 'text',
	DataUrl = 'base64',
	File = 'file',
}

type UploadInternalSyncStateType = File | string | ArrayBuffer;
@Component({
	selector: 'tp-file-upload',
	templateUrl: './tp-file-upload.component.html',
	styleUrls: ['./tp-file-upload.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: MatFormFieldControl, useExisting: TpFileUploadComponent }],
})
export class TpFileUploadComponent
	implements ControlValueAccessor, OnInit, OnDestroy, MatFormFieldControl<TpFileUploadComponent>
{
	@ViewChild('fileInput') public fileInput: ElementRef;
	@ViewChild('stdInput') public stdInput: ElementRef;

	@ContentChild(FileUploadCustomSlotDirective) public customContent?: FileUploadCustomSlotDirective;

	/*
	 * For customUI supported inputs
	 * 	-acceptedFileTypes
	 * 	-allowMultiple
	 *
	 * TODO: non-customUI part needs to be converted, so that every use case is same and differences is provided in ng-content
	 */

	// default is all files, can accept an array of string file extensions (eg. ['.xls', '.xlsx'])
	// will be used both as the "suggestion" of type and the enforcement (via a validator)
	@Input() public acceptedFileTypes: readonly string[] = ['*'];
	@Input() public allowMultiple = false;
	@Input() public startupMessage = appStrings.FILE_UPLOAD_STARTUP;
	@Input() public successMessage = appStrings.FILE_UPLOAD_SUCCESS;
	@Input() public failureMessage = appStrings.FILE_UPLOAD_FAILURE;
	@Input() public buttonText = appStrings.UICONTROL_TEXT_UPLOAD;
	@Input() public outputFormat: UploadOutputFormat = UploadOutputFormat.DataUrl;
	@Input() public validationErrors: IValidationError[] = []; // the types of validation errors on the file content that the user of this component can have

	@Input() public get placeholder() {
		return this._placeholder;
	}
	public set placeholder(pla: string) {
		this._placeholder = pla;
		//matFormField
		this.stateChanges.next();
	}
	private _placeholder = appStrings.FILE_UPLOAD_PLACEHOLDER;

	@Output() public fileSelectedEvent = new EventEmitter<File[]>();

	public disabled = false;
	public errorState = false;
	public uploadedFiles: UntypedFormControl;
	public uploadedFilenames: UntypedFormControl;
	public strings = appStrings;
	public hintMessages: Map<FileUploadState, string>;

	public uploadState = FileUploadState.Waiting;
	//weee, a poorly designed type and manually managed state /sigh
	private outputValue?: UploadInternalSyncStateType[]; // the content of the selected file or File object
	private shutdownSubject$ = new Subject<void>();
	public onChange: (value: any) => void;

	constructor(
		public ngControl: NgControl,
		private builder: UntypedFormBuilder,
		private changeDetectRef: ChangeDetectorRef,
		@Optional() private controlContainer: FormGroupDirective
	) {
		// inject the FormControl the is bound to this component through FormControl or formControlName
		this.ngControl.valueAccessor = this;
		this.uploadedFiles = this.builder.control(null);
		this.uploadedFilenames = this.builder.control('');
	}

	public ngOnInit(): void {
		this.hintMessages = new Map<FileUploadState, string>([
			[FileUploadState.Waiting, this.startupMessage],
			[FileUploadState.Loading, ''], // could be used to show loading progress
			[FileUploadState.Succeeded, this.successMessage],
			[FileUploadState.Failed, this.failureMessage],
		]);

		const existingValidator = this.ngControl.control.validator;
		//TODO: after angular 12 we can just append validators...
		if (existingValidator) {
			this.ngControl.control.setValidators([
				existingValidator,
				validFileTypeValidator(this.acceptedFileTypes, this.uploadedFiles),
			]);
		} else {
			this.ngControl.control.setValidators([
				validFileTypeValidator(this.acceptedFileTypes, this.uploadedFiles),
			]);
		}

		this.ngControl.control.updateValueAndValidity({ emitEvent: false });

		//TODO: this forwarding of errors does not work on _initial_ emission
		// could fix with a defer/ startWith, but I'm not even really seeing why it's worth forwarding...
		// keep the validity of internal control with that of the external one so that mat-error will work for external validation errors
		this.ngControl.control.statusChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => {
			// if there is validation error and user asks that to be handled through the passed in validationErrors, sync the validity
			const controlErrors = this.ngControl.control.errors;
			if (controlErrors) {
				this.uploadState = FileUploadState.Failed;
				this.uploadedFilenames.markAsTouched();
			}
			this.uploadedFilenames.setErrors(!controlErrors || !this.getErrorMessage() ? null : controlErrors);
		});

		// start file uploading when file is selected
		//TODO: this entire subscription is suspect since it's not actually in the ui
		this.uploadedFiles.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((value) => {
			this.handleFileSelectionChange(value);
			if (this.fileInput) {
				this.fileInput.nativeElement.value = '';
			}
		});

		this.setupMatFormFieldListeners();
		this.parentFormSubmitEventSubscription();
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	//TODO: why this instead of putting the formControl in the ui and listening for changes?
	public onFileChange(event: any): void {
		const files: File[] = event.target.files;
		const arr = Array.from(files); // FileList to Array
		this.uploadedFiles.patchValue(arr);
		this.uploadedFilenames.patchValue(
			arr.length < 2
				? arr.map((file: File) => file.name).join('')
				: `${arr.length} ${appStrings.FILES_SELECTED}`
		);
		this.updateErrorState();
	}

	// loading file content when file is selected
	private handleFileSelectionChange(files: File[]) {
		let loadedFileCounter = 0;

		this.outputValue = [];
		if (files?.length > 0) {
			//file selected event - to get filenames
			this.fileSelectedEvent.emit(files);

			for (const file of files) {
				const reader = new FileReader();
				switch (this.outputFormat) {
					case UploadOutputFormat.BinaryString:
						reader.readAsBinaryString(file);
						break;
					case UploadOutputFormat.Text:
						reader.readAsText(file);
						break;
					case UploadOutputFormat.File:
						this.outputValue.push(file);
						loadedFileCounter++;
						break;
					default:
						reader.readAsDataURL(file);
						break;
				}

				if (this.outputFormat !== UploadOutputFormat.File) {
					reader.onload = (result) => {
						this.uploadState = (result.target as FileReader).error
							? FileUploadState.Failed
							: FileUploadState.Succeeded;
						if (this.uploadState === FileUploadState.Succeeded) {
							this.outputValue.push((result.target as FileReader).result);

							// update form when all files are uploaded
							loadedFileCounter++;
							if (loadedFileCounter === files.length) {
								this.onChange(this.outputValue);
								// now the value updated and validation triggered, needs to keep the internal status reflected on UI
								this.changeDetectRef.detectChanges();
							}
						} else {
							this.onChange(null);
						}
					};
				}
			}

			// also need here for sync operation
			if (loadedFileCounter === files.length) {
				this.onChange(this.outputValue);
				// now the value updated and validation triggered, needs to keep the internal status reflected on UI
				this.changeDetectRef.detectChanges();
			}
		} else {
			this.onChange(null);
			this.outputValue = null;
			this.uploadState = FileUploadState.Waiting;
		}
	}

	public getErrorMessage(): string {
		let message;

		// the first validation error in the given list that can be found in the controls' errors list is displayed
		for (const error of this.validationErrors) {
			if (this.ngControl.control.hasError(error.errorCode)) {
				message = error.message;
				break;
			}
		}

		return message;
	}

	/**************************** ControlValueAccessor  *******************************/
	public writeValue(value: any): void {
		//value _must_ be an array
		//TODO: the empty array case is not being handled in much the same was as "null" isn't really being handled
		//should fix eventually
		if (value?.length) {
			this.uploadedFilenames.setValue(value[0]?.name, { emitEvent: false });
		}

		if (!value) {
			this.outputValue = null;
		}
	}

	public registerOnChange(fn: (value: any) => void): void {
		this.onChange = fn;
	}

	private onTouched = () => {};
	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	public setDisabledState?(isDisabled: boolean): void {
		this.disabled = isDisabled;
		if (this.disabled) {
			//TODO: why is this disabling uploadedFiles instead of uploadedFileNames. Changed it to both
			this.uploadedFiles.disable({ emitEvent: false });
			this.uploadedFilenames.disable({ emitEvent: false });
		} else {
			this.uploadedFiles.enable({ emitEvent: false });
			this.uploadedFilenames.enable({ emitEvent: false });
		}
		this.stateChanges.next();
	}

	/**************************** MatFormField  *******************************/

	//not part of the api, just custom code that will interact with it
	private setupMatFormFieldListeners() {
		this.uploadedFilenames.valueChanges
			.pipe(mapTo(undefined), takeUntil(this.shutdownSubject$))
			.subscribe(subjectSubscribeWithoutComplete(this.stateChanges));
	}

	public get value() {
		return this.uploadedFilenames.value; //TODO: ...sure
	}

	public stateChanges = new Subject<void>();

	static nextId = 0;

	@HostBinding() id = `mat-file-upload-${TpFileUploadComponent.nextId++}`;

	public focused = false;

	public onFocusIn(event: FocusEvent) {
		if (!this.focused) {
			this.focused = true;
			this.stateChanges.next();
		}
	}

	public onFocusOut(event: FocusEvent) {
		if (!this.fileInput.nativeElement.contains(event.relatedTarget as Element)) {
			this.focused = false;
			this.onTouched();
			this.stateChanges.next();
		}
	}

	public get empty() {
		return !this.uploadedFilenames.value;
	}

	@HostBinding('class.floating')
	public get shouldLabelFloat() {
		return this.focused || !this.empty;
	}

	// display only configuration that determine if the asterix show up
	// it's blocked by the button in this component soo... perma-false
	public get required() {
		return false;
	}

	public controlType = 'tp-file-upload';

	public setDescribedByIds(ids: string[]): void {
		//accessibility stuff
	}

	public onContainerClick(event: MouseEvent): void {
		//focus the input when the mat-form-field is clicked
		if (!this.customContent) {
			//TODO: switch to Renderer2
			this.stdInput.nativeElement.focus();
		}
		//TODO: this works, but it's awful
		// this.fileInput.nativeElement.click();
	}

	// Method that subscribes to the parent form's submit event
	private parentFormSubmitEventSubscription(): void {
		this.controlContainer?.ngSubmit.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => {
			if (this.ngControl) {
				this.updateErrorState();
			}
		});
	}

	// Method that updates the error state of the control
	// Based on https://material.angular.io/guide/creating-a-custom-form-field-control#errorstate
	private updateErrorState() {
		const parent = this.controlContainer;

		const oldState = this.errorState;
		const newState =
			!!this.ngControl.control.errors && (this.uploadedFilenames.touched || this.controlContainer.submitted);

		if (oldState !== newState) {
			this.errorState = newState;
			this.stateChanges.next();
		}
	}
}
